// Plaza events from rooms (docs/backend/rooms.md, "HTTP"): pitch nights, concerts and meetups on the plaza's stage.
// Reading: `GET /v1/events?world=plaza` (public, soonest first). Writing: `POST /v1/events` (schedule, Bearer session)
// and `POST /v1/events/:id/feature` (moderators). The site no longer joins the plaza socket to schedule.
// Rules kept here and enforced by rooms: hosts are signed-in 18+ accounts (or moderators), featuring needs a
// moderator, one event per plaza instance (rooms picks a free one). Only a live pitch night or concert takes over the
// other instances' stages, so scheduling ahead doesn't block anyone. The site never shows a concert's media (13–17
// accounts hear concert audio only in featured events; the plaza applies that).

import { api, ApiError } from "./api.ts";
import { playLink } from "./play.ts";

export const WORLD = "plaza";
/** The plaza's catalogue id: "Join in Threedom" opens it in the launcher. */
export const PLAZA_GAME = "g_plaza";

export type EventKind = "pitch" | "concert" | "meetup";

export interface PlazaEvent {
  id: string;
  kind: EventKind;
  title: string;
  /** unix ms */
  at: number;
  host: { id: string | null; name: string };
  status: "scheduled" | "live";
  /** unix ms */
  startedAt: number | null;
  featured: boolean;
  /** the plaza instance that holds it */
  instance: string;
  /** not in the calendar listing; present when rooms sends them */
  slots?: number;
  circles?: { n: number; seats: number } | null;
  /** wave 11: a catalogue row the stage screen loops between pitches ("halloween" on Halloween night) */
  showcase?: string;
  /** wave 11: a cosmetic everyone present while it's live unlocks */
  reward?: string;
  /** unix ms; an automatic event ends by itself then */
  endsAt?: number;
  /** wave 12: where in the Plaza (rooms' effective venue: the host's pick, else its rule) */
  venue?: "podium" | "tholos" | "terraces";
  /** wave 12: how many players RSVPed */
  rsvps?: number;
}

export interface Calendar {
  events: PlazaEvent[];
}

export const KIND_LABEL: Record<EventKind, string> = { pitch: "Pitch night", concert: "Concert", meetup: "Meetup" };
export const KIND_BLURB: Record<EventKind, string> = {
  pitch: "People take the stage one at a time and pitch what they made. A pitch can end with Play it now or Follow.",
  concert: "Live music on the plaza stage. Performers come up one after another.",
  meetup: "Circles of seats to sit and talk with the people around you.",
};

const INSTANCE = /^[1-9][0-9]{0,4}$/;
const KINDS: EventKind[] = ["pitch", "concert", "meetup"];

/** "Join in Threedom": the launcher opens the plaza, in the event's instance once the launcher reads `instance=`. */
export function joinLink(instance: string): string {
  const base = playLink(PLAZA_GAME);
  return INSTANCE.test(instance) ? `${base}?instance=${instance}` : base;
}

/** unix ms from rooms; a value that looks like seconds is taken as seconds. */
function ms(v: unknown): number | null {
  if (typeof v !== "number" || !Number.isFinite(v) || v <= 0) return null;
  return v < 1e12 ? v * 1000 : v;
}

/** One calendar entry, read defensively; null for anything that isn't a usable event. */
export function parseEvent(raw: unknown): PlazaEvent | null {
  if (!raw || typeof raw !== "object") return null;
  const e = raw as Record<string, unknown>;
  const instance = typeof e.instance === "number" ? String(e.instance) : typeof e.instance === "string" ? e.instance : "";
  const kind = KINDS.includes(e.kind as EventKind) ? (e.kind as EventKind) : "pitch";
  const status = e.status === "live" ? "live" : e.status === "scheduled" ? "scheduled" : null;
  const at = ms(e.at) ?? ms(e.startedAt);
  if (typeof e.id !== "string" || !e.id || !status || at === null || !INSTANCE.test(instance)) return null;
  const host = e.host && typeof e.host === "object" ? (e.host as Record<string, unknown>) : null;
  const out: PlazaEvent = {
    id: e.id,
    kind,
    title: typeof e.title === "string" && e.title.trim() ? e.title : KIND_LABEL[kind],
    at,
    host: { id: typeof host?.id === "string" ? host.id : null, name: typeof host?.name === "string" && host.name ? host.name : "Host" },
    status,
    startedAt: ms(e.startedAt),
    featured: e.featured === true,
    instance,
  };
  if (typeof e.slots === "number" && Number.isInteger(e.slots) && e.slots > 0) out.slots = e.slots;
  const c = e.circles && typeof e.circles === "object" ? (e.circles as Record<string, unknown>) : null;
  if (c && typeof c.n === "number" && typeof c.seats === "number") out.circles = { n: c.n, seats: c.seats };
  if (typeof e.showcase === "string" && /^[a-z0-9][a-z0-9-]{0,39}$/.test(e.showcase)) out.showcase = e.showcase;
  if (typeof e.reward === "string" && e.reward) out.reward = e.reward;
  if (e.venue === "podium" || e.venue === "tholos" || e.venue === "terraces") out.venue = e.venue;
  if (typeof e.rsvps === "number" && Number.isInteger(e.rsvps) && e.rsvps >= 0) out.rsvps = e.rsvps;
  const ends = ms(e.endsAt);
  if (ends !== null && ends > at) out.endsAt = ends;
  return out;
}

/** rooms' calendar answer → events, live first, then soonest; repeats and ended ones dropped. */
export function calendarFrom(raw: unknown): Calendar {
  const list = raw && typeof raw === "object" && Array.isArray((raw as { events?: unknown }).events) ? (raw as { events: unknown[] }).events : [];
  const seen = new Set<string>();
  const events: PlazaEvent[] = [];
  for (const r of list) {
    const e = parseEvent(r);
    if (!e || seen.has(e.id)) continue;
    seen.add(e.id);
    events.push(e);
  }
  events.sort((a, b) => (a.status === "live" ? 0 : 1) - (b.status === "live" ? 0 : 1) || a.at - b.at);
  return { events };
}

export async function loadCalendar(world = WORLD): Promise<Calendar> {
  return calendarFrom(await api("rooms", `/events?world=${encodeURIComponent(world)}`, { auth: false }));
}

/** "4 pitch slots", "3 circles of 8 seats", or null when rooms didn't say. */
export function sizeText(e: Pick<PlazaEvent, "kind" | "slots" | "circles">): string | null {
  if (e.kind === "meetup") return e.circles ? `${e.circles.n} ${e.circles.n === 1 ? "circle" : "circles"} of ${e.circles.seats} seats` : null;
  if (!e.slots) return null;
  return `${e.slots} ${e.kind === "concert" ? (e.slots === 1 ? "act" : "acts") : e.slots === 1 ? "pitch slot" : "pitch slots"}`;
}

// ---- time, in the viewer's zone --------------------------------------------------------------------------------

export function viewerZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

/** "19:30 EEST" (the zone's short name, so the time is never ambiguous). */
export function fmtTime(at: number, timeZone = viewerZone(), locale?: string): string {
  return new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit", timeZone, timeZoneName: "short" }).format(at);
}

/** The calendar day of `at` in `timeZone`, as YYYY-MM-DD (for grouping). */
export function dayKey(at: number, timeZone = viewerZone()): string {
  const p = new Intl.DateTimeFormat("en-CA", { year: "numeric", month: "2-digit", day: "2-digit", timeZone }).formatToParts(at);
  const v = (t: string) => p.find((x) => x.type === t)?.value ?? "";
  return `${v("year")}-${v("month")}-${v("day")}`;
}

/** "Today", "Tomorrow", or "Sat 18 Oct" in the viewer's zone. */
export function dayLabel(at: number, now = Date.now(), timeZone = viewerZone(), locale?: string): string {
  const k = dayKey(at, timeZone);
  if (k === dayKey(now, timeZone)) return "Today";
  if (k === dayKey(now + 86_400_000, timeZone)) return "Tomorrow";
  return new Intl.DateTimeFormat(locale, { weekday: "short", day: "numeric", month: "short", timeZone }).format(at);
}

/** Events grouped by day (live ones under "Happening now"), in order. */
export function groupByDay(events: PlazaEvent[], now = Date.now(), timeZone = viewerZone()): { label: string; events: PlazaEvent[] }[] {
  const groups: { key: string; label: string; events: PlazaEvent[] }[] = [];
  for (const e of events) {
    const key = e.status === "live" ? "live" : dayKey(e.at, timeZone);
    let g = groups.find((x) => x.key === key);
    if (!g) groups.push((g = { key, label: e.status === "live" ? "Happening now" : dayLabel(e.at, now, timeZone), events: [] }));
    g.events.push(e);
  }
  return groups.map(({ label, events }) => ({ label, events }));
}

/** A `datetime-local` value ("2026-10-18T19:30", the viewer's wall clock) → unix ms, or null. */
export function parseLocal(value: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return null;
  const t = new Date(value).getTime(); // a date-time string without a zone is local time
  return Number.isFinite(t) ? t : null;
}

/** unix ms → a `datetime-local` value in the viewer's zone. */
export function toLocalInput(at: number): string {
  const d = new Date(at);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export interface Draft {
  title: string;
  kind: EventKind;
  at: number | null;
  slots: number;
  circles: number;
  seats: number;
}

/** The checks rooms makes, before sending (rooms stays the authority). */
export function checkDraft(d: Draft, now = Date.now()): string | null {
  const title = d.title.trim();
  if (!title) return "Give the event a title.";
  if (title.length > 80) return "Keep the title to 80 characters.";
  if (d.at === null) return "Pick a date and time.";
  if (d.at < now - 60_000) return "That time has passed. Pick a time from now on.";
  if (d.at > now + 90 * 86_400_000) return "Events can be up to 90 days ahead.";
  if (d.kind !== "meetup" && (!Number.isInteger(d.slots) || d.slots < 1 || d.slots > 20)) return "Slots go from 1 to 20.";
  if (d.kind === "meetup" && (!Number.isInteger(d.circles) || d.circles < 1 || d.circles > 8)) return "Circles go from 1 to 8.";
  if (d.kind === "meetup" && (!Number.isInteger(d.seats) || d.seats < 3 || d.seats > 12)) return "Seats per circle go from 3 to 12.";
  return null;
}

/** The body of `POST /v1/events` (rooms picks the instance: the lowest with no event of its own). */
export function scheduleBody(d: Draft, hostName?: string): Record<string, unknown> {
  const m: Record<string, unknown> = { title: d.title.trim(), kind: d.kind, at: d.at };
  if (d.kind === "meetup") Object.assign(m, { circles: d.circles, seats: d.seats });
  else m.slots = d.slots;
  if (hostName) m.hostName = hostName.slice(0, 32);
  return m;
}

/** Plain words for what rooms says (codes from docs/backend/rooms.md). */
export function roomMessage(code: string, fallback = ""): string {
  const m: Record<string, string> = {
    not_allowed: fallback || "You can't do that here.",
    conflict: "That plaza room got an event at the same moment. Try again and we'll use another one.",
    unauthorized: "Please sign in again.",
    session_revoked: "Your session has ended. Please sign in again.",
    accounts_unreachable: "We couldn't check your account just now. Try again in a moment.",
    rate_limited: "That was a lot of changes at once. Wait a moment and try again.",
    bad_request: fallback || "Rooms didn't accept that.",
    not_found: "That event isn't on the calendar any more. Reload it.",
    rsvps_full: "That night is full up on RSVPs. You can still come along.",
  };
  return m[code] ?? (fallback || "Something went wrong with the plaza.");
}

/** An ApiError from rooms, in plain words. */
export function eventError(e: unknown): string {
  if (e instanceof ApiError) return roomMessage(e.code, e.message);
  return e instanceof Error ? e.message : String(e);
}

export async function scheduleEvent(d: Draft, hostName?: string, world = WORLD): Promise<PlazaEvent | null> {
  const r = await api<{ event?: unknown }>("rooms", `/events?world=${encodeURIComponent(world)}`, { body: scheduleBody(d, hostName) });
  return parseEvent(r?.event);
}

export async function featureEvent(id: string, on: boolean, world = WORLD): Promise<void> {
  await api("rooms", `/events/${encodeURIComponent(id)}/feature?world=${encodeURIComponent(world)}`, { body: { on } });
}

/** RSVP to a rooms event (signed in; docs/backend/rooms.md "Wave 12: RSVPs and venues"). Rooms reminds RSVPed players when it starts. */
export async function setRsvp(id: string, going: boolean, world = WORLD): Promise<{ going: boolean; rsvps: number }> {
  const r = await api<{ going?: unknown; rsvps?: unknown }>("rooms", `/events/${encodeURIComponent(id)}/rsvp?world=${encodeURIComponent(world)}`, going ? { body: {} } : { method: "DELETE" });
  return { going: r?.going === true, rsvps: typeof r?.rsvps === "number" ? r.rsvps : 0 };
}
